import React, { useRef, useState } from 'react'
import { Transfer as AntTransfer } from 'antd'
import type { TransferProps } from 'antd/es/transfer'
import { TransferKey } from 'antd/es/transfer/interface'
import { AutoSizer, List as VirtualList } from 'react-virtualized'
import {
  ButtonWrapper,
  Content,
  List,
  ObjectCheck,
  ObjectItem,
  ObjectTitle,
  TransferContainer,
  VirtualListWrapper,
} from './styled'
import { InvisibleButton } from '../Button/Button'
import { SmallInput } from '../Input/Input'
import { Icon } from '../../Icon/Icon'
import { theme } from '@/app/styles/theme'

interface CustomTransferProps
  extends Omit<TransferProps, 'onChange' | 'targetKeys' | 'children'> {
  value?: TransferKey[]
  onChange?: (value: TransferKey[]) => void
  onSelectAll?: () => void
  onDeselectAll?: () => void
  selectedCount?: number
  onSelectedCountChange?: (count: number) => void
  idField?: string
  nameField?: string
}

const ITEM_HEIGHT = 40
const VIRTUALIZATION_THRESHOLD = 100

export const Transfer: React.FC<CustomTransferProps> = ({
  dataSource = [],
  value = [],
  onChange,
  onSelectAll,
  onDeselectAll,
  selectedCount,
  onSelectedCountChange,
  idField = 'id',
  nameField = 'name',
  ...props
}) => {
  const transferRef = useRef<HTMLDivElement>(null)
  const [leftSearchValue, setLeftSearchValue] = useState('')
  const [rightSearchValue, setRightSearchValue] = useState('')

  const getId = (item: any) => item[idField] || item.key
  const getName = (item: any) => item[nameField] || item.title || item.key

  const handleCheckboxChange = (itemId: TransferKey, checked: boolean) => {
    if (checked) {
      const newValue = [...value, itemId]
      onChange?.(newValue)
      onSelectedCountChange?.(newValue.length)
    } else {
      const newValue = value.filter((id) => id !== itemId)
      onChange?.(newValue)
      onSelectedCountChange?.(newValue.length)
    }
  }

  const handleSelectAll = () => {
    const allIds = dataSource.map((item) => getId(item))
    onChange?.(allIds)
    onSelectedCountChange?.(allIds.length)
    onSelectAll?.()
  }

  const handleDeselectAll = () => {
    onChange?.([])
    onSelectedCountChange?.(0)
    onDeselectAll?.()
  }

  const filterLeftItems = (items: typeof dataSource, searchValue: string) => {
    if (!searchValue) return items

    return items.filter(
      (item) =>
        getName(item)?.toLowerCase().includes(searchValue.toLowerCase()) ||
        getId(item)
          ?.toString()
          .toLowerCase()
          .includes(searchValue.toLowerCase())
    )
  }

  const filterRightItems = (items: typeof dataSource, searchValue: string) => {
    if (!searchValue) return items

    return items.filter(
      (item) =>
        getName(item)?.toLowerCase().includes(searchValue.toLowerCase()) ||
        getId(item)
          ?.toString()
          .toLowerCase()
          .includes(searchValue.toLowerCase())
    )
  }

  const VirtualizedItem = ({
    item,
    $isSelected,
    $isLeft,
    style,
  }: {
    item: any
    $isSelected: boolean
    $isLeft: boolean
    style: React.CSSProperties
  }) => {
    const itemId = getId(item)
    const itemName = getName(item)

    return (
      <div style={style}>
        <ObjectItem
          $isSelected={$isSelected}
          $isLeft={$isLeft}
          align="center"
          gap={16}
          onClick={() => {
            handleCheckboxChange(itemId, !$isSelected)
          }}
        >
          <ObjectTitle>{itemName}</ObjectTitle>
          {$isLeft ? (
            <ObjectCheck>
              <Icon
                name={$isSelected ? 'object_check' : 'object_add'}
                size={16}
              />
            </ObjectCheck>
          ) : (
            <ObjectCheck>
              <Icon name="object_clear" size={16} />
            </ObjectCheck>
          )}
        </ObjectItem>
      </div>
    )
  }

  const RegularList = ({
    items,
    $isLeft,
  }: {
    items: any[]
    $isLeft: boolean
  }) => (
    <List className="scrollable">
      {items.map((item) => {
        const itemId = getId(item)
        const itemName = getName(item)
        const isSelected = value.includes(itemId)

        return (
          <ObjectItem
            key={itemId}
            $isSelected={isSelected}
            $isLeft={$isLeft}
            align="center"
            gap={16}
            onClick={() => {
              handleCheckboxChange(itemId, !isSelected)
            }}
          >
            <ObjectTitle>{itemName}</ObjectTitle>
            {$isLeft ? (
              <ObjectCheck>
                <Icon
                  name={isSelected ? 'object_check' : 'object_add'}
                  size={16}
                />
              </ObjectCheck>
            ) : (
              <ObjectCheck>
                <Icon name="object_clear" size={16} />
              </ObjectCheck>
            )}
          </ObjectItem>
        )
      })}
    </List>
  )

  return (
    <TransferContainer ref={transferRef}>
      <AntTransfer
        {...props}
        dataSource={dataSource}
        targetKeys={[]}
        showSelectAll={false}
        oneWay={false}
        filterOption={(inputValue, option) =>
          getName(option)?.toLowerCase().includes(inputValue.toLowerCase()) ||
          getId(option)?.toLowerCase().includes(inputValue.toLowerCase())
        }
      >
        {({ direction }) => {
          const itemsToShow =
            direction === 'left'
              ? filterLeftItems(dataSource, leftSearchValue)
              : filterRightItems(
                  dataSource.filter((item) => value.includes(getId(item))),
                  rightSearchValue
                )

          const isSelectAllDisabled =
            direction === 'left' &&
            (dataSource.filter((item) => !value.includes(getId(item)))
              .length === 0 ||
              !onSelectAll)

          const isDeselectAllDisabled =
            direction === 'right' && (value.length === 0 || !onDeselectAll)

          const shouldUseVirtualization =
            itemsToShow.length > VIRTUALIZATION_THRESHOLD

          return (
            <Content vertical gap={16}>
              <SmallInput
                placeholder={
                  direction === 'left'
                    ? 'Поиск для добавления'
                    : 'Поиск для удаления'
                }
                value={
                  direction === 'left' ? leftSearchValue : rightSearchValue
                }
                prefix={<Icon name="input_search" size={16} />}
                onChange={(e) => {
                  if (direction === 'left') {
                    setLeftSearchValue(e.target.value)
                  } else {
                    setRightSearchValue(e.target.value)
                  }
                }}
              />

              {shouldUseVirtualization ? (
                <VirtualListWrapper>
                  <AutoSizer>
                    {({ height, width }) => (
                      <VirtualList
                        width={width}
                        height={height}
                        rowCount={itemsToShow.length}
                        rowHeight={ITEM_HEIGHT}
                        rowRenderer={({ index, key, style }) => {
                          const item = itemsToShow[index]
                          const itemId = getId(item)
                          const isSelected = value.includes(itemId)

                          return (
                            <VirtualizedItem
                              key={key}
                              item={item}
                              $isSelected={isSelected}
                              $isLeft={direction === 'left'}
                              style={style}
                            />
                          )
                        }}
                      />
                    )}
                  </AutoSizer>
                </VirtualListWrapper>
              ) : (
                <RegularList
                  items={itemsToShow}
                  $isLeft={direction === 'left'}
                />
              )}

              <ButtonWrapper justify="flex-end">
                {direction === 'left' ? (
                  <InvisibleButton
                    onClick={handleSelectAll}
                    disabled={isSelectAllDisabled}
                    icon={
                      <Icon
                        name="add_btn"
                        size={16}
                        fill={
                          isSelectAllDisabled
                            ? theme.colors.text
                            : theme.colors.primary
                        }
                      />
                    }
                  >
                    Выбрать все
                  </InvisibleButton>
                ) : (
                  <InvisibleButton
                    onClick={handleDeselectAll}
                    disabled={isDeselectAllDisabled}
                    icon={
                      <Icon
                        name="clear_btn"
                        size={16}
                        fill={
                          isDeselectAllDisabled
                            ? theme.colors.text
                            : theme.colors.primary
                        }
                      />
                    }
                  >
                    Очистить все
                  </InvisibleButton>
                )}
              </ButtonWrapper>
            </Content>
          )
        }}
      </AntTransfer>
    </TransferContainer>
  )
}
